import { useCallback, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import axios from 'axios';

import { handleFileAdd, handleDeleteRegistration, handleFileRemove, handleFileReplace } from '../misc/requests';
import AppConfig from '../providers/AppConfig';
import errorService from '../helpers/errors.helper';
import { BaseLoader } from '../components';
import { successToast, warningToast } from '../store/slices/toaster.slice';
import { setCompletedState, setSubmittedState } from '../store/slices/requestMeta.slice';
import { hydrateList as hydrateListAction, updateOnList } from '../store/slices/list.slice';
import { RequestContext } from './Request.context';
import { useRequestList } from './RequestList.context';

const { Provider } = RequestContext;

// Helper: handle request CRUD (replaces the old handleRequest + createRequest/updateRequest/submitRequest thunks)
const handleRequest = async (method, data, url, dispatch) => {
  const caseId = data.case_id ? `${data.case_id}/` : '';
  try {
    const response = await axios({ method, url: `/${url}/${caseId}`, data });
    // Dispatch a generic action type that both app slices handle
    // (both createSlice calls use name: 'request' and have updateRequestField reducer)
    dispatch({ type: 'request/updateRequestField', payload: response.data.data });
    return response.data;
  } catch (error) {
    errorService.backendValidationError(error);
  }
};

export const RequestProvider = ({ url, children }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const request = useSelector((state) => state.request);
  const amendments = useSelector((state) => state.amendments);
  const metadata = useSelector((state) => state.requestMeta);
  const resources = useSelector((state) => state.resources);
  const { list } = useRequestList();
  const fileTypeData = useSelector((state) => state.resources.file_type?.data);
  const fileUploadTypes = useMemo(() => {
    if (!fileTypeData) return {};
    return fileTypeData.reduce((acc, next) => {
      acc[next.name.toUpperCase().replace(/ /g, '_')] = next.id;
      return acc;
    }, {});
  }, [fileTypeData]);

  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  const validateFormFnRef = useRef(null);

  const handleLoading = useCallback((bool) => {
    setLoading(bool);
  }, []);

  const getAmendments = useCallback(
    async (caseId) => {
      if (!caseId) return;
      handleLoading(true);
      try {
        const { api } = await import('../store/api');
        await dispatch(api.endpoints.getAmendments.initiate(caseId));
      } catch (error) {
        errorService.showBackendErrors(error);
      } finally {
        handleLoading(false);
      }
    },
    [dispatch, handleLoading]
  );

  const saveStep = async (values) => {
    handleLoading(true);

    try {
      const payload = values ? { ...values, case_id: request.case_id } : { ...request };
      if (payload.date_of_birth) {
        payload.date_of_birth = payload.date_of_birth.format(AppConfig.defaultBackendDateFormat);
      }

      let result;
      if (request.case_id) {
        result = await handleRequest('put', payload, url, dispatch);
      } else {
        result = await handleRequest('post', payload, url, dispatch);
      }

      if (result) {
        dispatch(updateOnList(result.data));
        return result.data;
      }
    } catch (error) {
      errorService.showBackendErrors(error);
    } finally {
      handleLoading(false);
    }

    return false;
  };

  const restoreCompaniesOnBackNavigation = async (companiesIds) => {
    handleLoading(true);
    try {
      const path = `/${url}/${request.case_id}/restore-companies/`;
      const { data } = await axios.post(path, companiesIds);
      dispatch(updateOnList(data.data));
      dispatch({ type: 'request/updateRequestField', payload: data.data });
      return data;
    } catch (error) {
      errorService.showBackendErrors(error);
    } finally {
      handleLoading(false);
    }
    return false;
  };

  const clearAfterSubmitForm = async () => {
    dispatch(setSubmittedState(true));
    dispatch(setCompletedState(false));
    dispatch({ type: 'request/clearForm' });
  };

  const submitForm = async (
    isAmendment = false,
    isRenewal = false,
    isEmployerChange = false,
    additionalRecipients = ''
  ) => {
    handleLoading(true);

    try {
      const {
        confirmations,
        comment,
        approvals,
        case_id,
        additional_recipients,
        payment,
        security_type,
        electronic_delivery_allowed,
      } = store.getState().request;

      let payload;

      if (url === 'prospectuses') {
        payload = { comment, electronic_delivery_allowed, security_type };
      }

      if (url === 'client-advisors') {
        payload = { confirmations, comment, approvals, case_id, additional_recipients, payment };
      }

      if (isRenewal || isEmployerChange) {
        payload = {
          payment: { method: 1, voucher_code: '', status: null },
        };
      }

      if (isAmendment) {
        payload = { payment, additional_recipients: additionalRecipients };
      }

      payload.case_id = case_id;

      if (isRenewal || isEmployerChange) {
        const result = await handleRequest('put', payload, url, dispatch);
        if (result) dispatch(updateOnList(result.data));
      }

      if (!(isRenewal || isEmployerChange)) {
        const result = await handleRequest('patch', payload, url, dispatch);
        if (result) dispatch(updateOnList(result.data));
      }

      if (isAmendment || isRenewal || isEmployerChange) {
        const { api } = await import('../store/api');
        dispatch(api.util.invalidateTags(['Amendments']));
      }

      handleLoading(false);
      return true;
    } catch (error) {
      if (url === 'client-advisors' && error.response && error.response.status === 406) {
        handleLoading(false);
        return { status: 406 };
      }
      errorService.showBackendErrors(error);
      handleLoading(false);
      return false;
    }
  };

  const addFile = async (data, config, docType = 'documents') => {
    try {
      const { case_id } = request;
      return handleFileAdd(`${url}/${case_id}/${docType}/`, data, config);
    } catch (e) {
      throw new Error('Failed to add file', { cause: e });
    }
  };

  const replaceFile = (data, config, replacedFileId) => {
    try {
      const { case_id } = request;
      return handleFileReplace(`${url}/${case_id}/documents/${replacedFileId}/`, data, config);
    } catch (e) {
      throw new Error('Failed to replace file', { cause: e });
    }
  };

  const removeFile = async (fileId) => {
    try {
      await handleFileRemove(request.case_id, fileId, url);
    } catch (e) {
      throw new Error('Failed to remove file', { cause: e });
    }
  };

  const cancelAmendment = async (caseId) => {
    handleLoading(true);
    try {
      await axios.delete(`/client-advisors/${caseId}/`);
      const { data: fresh } = await axios.get(`/client-advisors/${caseId}/`);
      dispatch({ type: 'request/updateRequestField', payload: fresh.data });
    } catch (error) {
      errorService.showBackendErrors(error);
    } finally {
      handleLoading(false);
    }
  };

  const hydrateList = useCallback(
    async (path) => {
      try {
        const response = await axios.get(path || `/${url}/`);
        dispatch(hydrateListAction(response.data.data));
      } catch (error) {
        errorService.showBackendErrors(error);
      }
    },
    [dispatch, url]
  );

  const deleteRegistration = async (data) => {
    try {
      const { case_id } = request;
      const result = await handleDeleteRegistration(`/client-advisors/${case_id}/`, {
        deletionDate: data.deletion_date.format(AppConfig.defaultBackendDateFormat),
        files: data.files,
        documentType: data.type,
      });
      const { data: fresh } = await axios.get(`/client-advisors/${case_id}/`);
      dispatch({ type: 'request/updateRequestField', payload: fresh.data });
      const { api } = await import('../store/api');
      dispatch(api.util.invalidateTags(['Amendments']));
      return result;
    } catch (e) {
      throw new Error('Failed to delete registration', { cause: e });
    }
  };

  const updateRequestField = useCallback(
    (field) => {
      dispatch({ type: 'request/updateRequestField', payload: field });
    },
    [dispatch]
  );

  const clearForm = useCallback(() => {
    dispatch({ type: 'request/clearForm' });
  }, [dispatch]);

  return (
    <Provider
      value={{
        request,
        amendments,
        metadata,
        resources,
        list,
        url,
        cancelAmendment,
        hydrateList,
        deleteRegistration,
        updateRequestField,
        setCompletedState: (val) => dispatch(setCompletedState(val)),
        setSubmittedState: (val) => dispatch(setSubmittedState(val)),
        clearForm,
        clearAfterSubmit: clearAfterSubmitForm,
        successToast,
        warningToast,
        submitForm,
        saveStep,
        addFile,
        replaceFile,
        removeFile,
        setLoading: handleLoading,
        restoreCompanies: restoreCompaniesOnBackNavigation,
        getAmendments,
        formRef,
        validateFormFnRef,
        FILE_UPLOAD_TYPES: fileUploadTypes,
      }}
    >
      <BaseLoader loading={loading} large>
        {children}
      </BaseLoader>
    </Provider>
  );
};

export default RequestProvider;
